// The download page (zones/download/zone.json) in the zone framework (docs/build/zones.md): main.ts imports this module for
// its side effects. It registers the zone's own sections:
//   platform        Download Banger: a tab per system (this one selected), the command or button, three features;
//   launcher-video  a closer loop of the app; hidden until the capture exists;
//   whats-new       the newest release notes (notes.ts); hidden while there are none;
//   requirements    system requirements, files and checksums, folded away;
//   questions       the short FAQ, without terminal commands.
// The release state is /downloads/latest.txt (what release-ops published), each system with its honest state; no dates.
//
// Section kinds are global, so each handler draws only for this zone and hands any other zone to whatever was
// registered before it.

import { gameById, getCatalogue, isComingSoon, type Game } from "../../catalogue.ts";
import { h, toast } from "../../dom.ts";
import { archLabel, fileName, formatBytes, loadBuilds, offers, type Build, type Offer } from "../../downloads.ts";
import { icon } from "../../icons.ts";
import { alreadyInstalled, downloadAction, freeAnswer, phonePrimary, REQUIREMENTS, waitlistForm } from "../../pages/download.ts";
import { detectPlatform } from "../../play.ts";
import { selling } from "../../selling.ts";
import { promoBlock } from "../promo.ts";
import { registerSection, sectionRenderer, zoneById, type SectionContext, type SectionRender } from "../registry.ts";
import { mediaUrl, zoneInSeason, type Zone } from "../spec.ts";
import { buildLabel, gateAction, gateQuestions, INSTALL_COMMAND, installerLine, usesInstaller, gatePlatform, platformState, promoCopy, type GatePlatform } from "./gate.ts";
import { latestNote, RELEASE_NOTES } from "./notes.ts";
import { ensureGateStyle } from "./style.ts";

export const ZONE_ID = "download";

// the zone's stylesheet goes in at load, so the phone hero height is right before any section resolves
ensureGateStyle();

/** The launcher's own loop for `launcher-video` (a headless capture, requested from the launcher track). None yet. */
export interface LauncherLoop {
  src: string;
  poster: string;
  label: string;
}
export const LAUNCHER_LOOP = null as LauncherLoop | null;

function own(kind: string, render: (ctx: SectionContext) => HTMLElement | null | Promise<HTMLElement | null>): void {
  const before: SectionRender | undefined = sectionRenderer(kind);
  registerSection(kind, (ctx) => {
    if (ctx.zone.id !== ZONE_ID) return before ? before(ctx) : null;
    ensureGateStyle();
    return render(ctx);
  });
}

/** The visitor's platform; `?platform=mac|windows|linux|mobile` forces one (screenshots, checking by hand). */
export function visitorPlatform(): GatePlatform {
  return gatePlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0, new URLSearchParams(location.search).get("platform"), detectPlatform);
}

let buildsOnce: Promise<Build[]> | null = null;
const builds = () => (buildsOnce ??= loadBuilds());

// ---- platform ----

function heading(game: Game | undefined, action: ReturnType<typeof downloadAction>): string {
  if (!game) return "Download Banger";
  if (action === "tip") return `Tip the creator of ${game.title} in Banger`;
  if (action === "notify") return `Get told when ${game.title} is out`;
  return `Play ${game.title} in Banger`;
}

/** A tab's badge: only what isn't a full release says so, in one word. */
function tabBadge(o: Offer): HTMLElement | null {
  if (o.state === "early") return h("span", { class: "gate-badge early" }, "Beta");
  if (o.state === "test") return h("span", { class: "gate-badge warn" }, "Test");
  return null;
}

/** One system's panel: the command (macOS, Linux) or the big button (Windows), and the files as a quiet second. */
function panel(o: Offer, game: Game | undefined): (HTMLElement | null)[] {
  const st = platformState(o);
  const note = o.state === "test" ? h("p", { class: "gate-state-warn" }, st.note) : null;
  if (!o.builds.length) {
    if (o.platform === "windows") return [h("p", { class: "gate-fine" }, st.note), waitlistForm("-gate")];
    return [h("p", { class: "gate-fine" }, st.note), browserButton(game)];
  }
  if (usesInstaller(o)) return [note, installerBlock(o), fileLinks(o)];
  const b = o.builds;
  return [
    note,
    h(
      "div",
      { class: "gate-buttons" },
      b.map((x) => h("a", { class: "btn btn-big gate-btn", href: x.url, download: "" }, icon("download", 20), buildLabel(o, x.url))),
    ),
    h("p", { class: "gate-fine" }, b.map((x) => `${archLabel(x.platform)} · ${formatBytes(x.bytes)}`).join("; "), " · ", h("a", { href: "#gate-requirements" }, "Checksums")),
  ];
}

/** The one-line installer with a Copy button: the primary action on macOS and Linux. */
function installerBlock(o: Offer): HTMLElement {
  const copy = h(
    "button",
    {
      type: "button",
      class: "btn gate-copy",
      "aria-label": "Copy the install command",
      onclick: async () => {
        try {
          await navigator.clipboard.writeText(INSTALL_COMMAND);
          toast("Copied: paste it into your terminal");
        } catch {
          toast("Couldn't copy; select the command instead");
        }
      },
    },
    icon("copy", 18),
    h("span", null, "Copy"),
  );
  return h(
    "div",
    { class: "gate-install" },
    h("div", { class: "gate-cmd" }, h("code", null, h("span", { class: "gate-prompt", "aria-hidden": "true" }, "$ "), INSTALL_COMMAND), copy),
    h("p", { class: "gate-fine" }, installerLine(o), " ", h("a", { href: "/install.sh" }, "Read the script"), "."),
  );
}

/** The files themselves, second: the disk image or the two packages. */
function fileLinks(o: Offer): HTMLElement {
  const b = o.builds;
  const many = b.length > 1;
  return h(
    "p",
    { class: "gate-fine gate-alt" },
    "Or download manually: ",
    b.map((x, i) => [
      i ? " · " : "",
      h("a", { href: x.url, download: "" }, icon("download", 14), o.platform === "mac" ? (many ? `${archLabel(x.platform)} .${x.format}` : `.${x.format}`) : buildLabel(o, x.url)),
      ` ${formatBytes(x.bytes)}`,
    ]),
    many && o.platform === "linux" ? " (install both)" : "",
  );
}

function browserButton(game: Game | undefined): HTMLElement {
  const web = game && !isComingSoon(game) && game.runtime === "web" && /^https:\/\//.test(game.url) ? game.url : null;
  return web
    ? h("a", { class: "btn btn-big", href: web, target: "_blank", rel: "noopener" }, icon("play", 20), `Play ${game!.title} in your browser`)
    : h("a", { class: "btn btn-big", href: "/" }, icon("play", 20), "Play in your browser");
}

/** Every system as a tab, this computer's selected: one page, no cards (Ollama's download page is the model). */
function platformTabs(all: Offer[], selected: Offer, game: Game | undefined): HTMLElement {
  const tabs: HTMLButtonElement[] = [];
  const panels: HTMLElement[] = [];
  const select = (i: number) => {
    tabs.forEach((t, j) => {
      t.setAttribute("aria-selected", String(i === j));
      t.tabIndex = i === j ? 0 : -1;
    });
    panels.forEach((p, j) => (p.hidden = i !== j));
  };
  all.forEach((o, i) => {
    const on = o === selected;
    tabs.push(
      h(
        "button",
        {
          type: "button",
          role: "tab",
          class: "gate-tab",
          id: `gate-t-${o.platform}`,
          "aria-controls": `gate-p-${o.platform}`,
          "aria-selected": String(on),
          tabindex: on ? "0" : "-1",
          onclick: () => select(i),
          onkeydown: (e: Event) => {
            const k = (e as KeyboardEvent).key;
            const d = k === "ArrowRight" ? 1 : k === "ArrowLeft" ? -1 : 0;
            if (!d) return;
            const n = (i + d + all.length) % all.length;
            select(n);
            tabs[n]!.focus();
          },
        },
        o.label,
        tabBadge(o),
      ),
    );
    panels.push(h("div", { class: "gate-panel", role: "tabpanel", id: `gate-p-${o.platform}`, "aria-labelledby": `gate-t-${o.platform}`, hidden: !on }, panel(o, game)));
  });
  return h("div", { class: "gate-act" }, h("div", { class: "gate-tabs", role: "tablist", "aria-label": "Your computer" }, tabs), panels);
}

const FEATURES = [
  ["library", "One library", "Every web game you love, with your progress and favourites in one place."],
  ["users", "Friends and parties", "See what your friends are playing and join them in a click."],
  ["play", "Press Play", "Games open in their own window. No tabs, no hunting for links."],
] as const;

own("platform", async (ctx) => {
  const query = new URLSearchParams(location.search);
  const gameId = query.get("game");
  const [cat, list] = await Promise.all([gameId ? getCatalogue().catch(() => null) : Promise.resolve(null), builds()]);
  const game = gameId && cat ? gameById(cat, gameId) : undefined;
  const all = offers(list);
  const gate = gateAction(visitorPlatform(), all);
  const action = downloadAction(query.get("for"), !!game && isComingSoon(game), selling());
  const phone = gate.kind === "browser";
  const shot = mediaUrl(ctx.zone.hero.poster);
  return h(
    "section",
    { class: "gate", "data-gate": gate.kind, "aria-labelledby": "gate-h" },
    h(
      "div",
      { class: "gate-in" },
      h("img", { class: "gate-mark", src: "/mark/banger-key-128.webp", srcset: "/mark/banger-key-128.webp 1x, /mark/banger-key-256.webp 2x", alt: "", width: 64, height: 64 }),
      h("h1", { id: "gate-h", class: "gate-h" }, heading(game, action)),
      h("p", { class: "gate-lead" }, phone ? "Banger is for computers. On your phone, every game plays in your browser." : "Every web game you love, in one app."),
      // a phone never gets a download (gate.ts): play here, or send the page to a computer
      phone ? phonePrimary(game) : platformTabs(all, gate.offer, game),
      game && !phone ? alreadyInstalled(game, action) : null,
    ),
    shot ? h("img", { class: "gate-shot", src: shot, alt: ctx.zone.hero.alt, loading: "lazy", decoding: "async", width: 2400, height: 1500 }) : null,
    h(
      "ul",
      { class: "gate-perks", role: "list" },
      FEATURES.map(([ic, t, d]) => h("li", null, h("span", { class: "gate-perk-icon" }, icon(ic, 20)), h("strong", null, t), h("span", null, d))),
    ),
  );
});

// ---- launcher-video ----

own("launcher-video", () => {
  const loop = LAUNCHER_LOOP;
  if (!loop) return null;
  const still = document.querySelector(`[data-zone="${ZONE_ID}"]`)?.getAttribute("data-motion") === "still";
  return h(
    "section",
    { class: "gate-film", "aria-label": loop.label },
    still
      ? h("img", { src: loop.poster, alt: loop.label, loading: "lazy" })
      : h("video", { src: loop.src, poster: loop.poster, muted: true, loop: true, playsinline: true, autoplay: true, preload: "metadata", "aria-label": loop.label }),
  );
});

// ---- whats-new ----

own("whats-new", () => {
  const n = latestNote(RELEASE_NOTES);
  if (!n) return null;
  const day = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${n.date}T12:00:00Z`));
  return h(
    "section",
    { class: "gate-new", "aria-labelledby": "gate-new-h" },
    h("p", { class: "zone-eyebrow" }, `What's new · ${n.version} · ${day}`),
    h("h2", { id: "gate-new-h", class: "zone-h2" }, n.title),
    h("ul", { class: "gate-new-list", role: "list" }, n.items.map((i) => h("li", null, h("strong", null, i.title), h("p", null, i.body)))),
  );
});

// ---- requirements ----

own("requirements", async () => {
  const all = offers(await builds());
  const status = (o: Offer | undefined) => {
    const st = o ? platformState(o) : null;
    return st ? h("span", { class: `gate-badge ${st.tone}` }, st.badge) : null;
  };
  return h(
    "details",
    { class: "gate-reqs", id: "gate-requirements" },
    h("summary", null, h("span", { class: "zone-h2" }, "Requirements and checksums"), h("span", { class: "gate-dim" }, "macOS, Linux, Windows")),
    h(
      "div",
      { class: "gate-req-grid" },
      REQUIREMENTS.map(([name, os, cpu, disk]) => {
        const o = all.find((x) => x.label === name);
        return h(
          "article",
          { class: "gate-req" },
          h("div", { class: "gate-req-head" }, h("h3", null, name), status(o)),
          h("dl", null, h("dt", null, "System"), h("dd", null, os), h("dt", null, "Processor"), h("dd", null, cpu), h("dt", null, "Disk"), h("dd", null, disk)),
          o && o.builds.length
            ? h(
                "ul",
                { class: "gate-files", role: "list" },
                o.builds.map((b) =>
                  h(
                    "li",
                    null,
                    h("a", { href: b.url, download: "" }, icon("download", 14), fileName(b.url)),
                    h("span", { class: "gate-dim" }, ` ${formatBytes(b.bytes)}`),
                    h("code", { class: "gate-sum", title: b.sha256 }, `SHA-256 ${b.sha256.slice(0, 12)}…`),
                    h("a", { href: `${b.url}.sha256` }, "full checksum"),
                  ),
                ),
              )
            : null,
        );
      }),
    ),
    h("p", { class: "gate-fine" }, "Every computer also needs a graphics card from the last few years and an internet connection."),
  );
});

// ---- questions ----

own("questions", async () => {
  const qa = gateQuestions(offers(await builds()), freeAnswer(selling()));
  return h(
    "section",
    { class: "gate-faq", "aria-labelledby": "gate-faq-h" },
    h("h2", { id: "gate-faq-h", class: "zone-h2" }, "Questions"),
    qa.map(([q, a]) => h("details", { class: "gate-q" }, h("summary", null, q), h("p", null, a))),
  );
});

// ---- promos ----

own("promos", async (ctx) => {
  let rowIds: Set<string> | undefined;
  try {
    rowIds = new Set((await ctx.catalogue()).rows?.map((r) => r.id) ?? []);
  } catch {
    rowIds = undefined;
  }
  const platform = visitorPlatform();
  const phone = platform === "mobile";
  // a test build can't get you into the Plaza night (it isn't your account), so it doesn't count
  const g = gateAction(platform, offers(await builds()));
  const canInstall = g.kind === "download" && g.offer.state !== "test";
  const list = ctx.zone.crossPromos
    .map((p) => ({ p, z: zoneById(p.zone) }))
    .filter((x): x is { p: (typeof x)["p"]; z: Zone } => !!x.z && x.z.id !== ctx.zone.id && zoneInSeason(x.z, ctx.now, rowIds));
  if (!list.length) return null;
  return h("div", { class: "zone-promos" }, list.map(({ p, z }) => promoBlock(z, p.placement, promoCopy(z, p.copy, phone, undefined, canInstall), undefined, phone && z.id === "open-haunt" ? "Play the haunts" : undefined, p.arg)));
});
